import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { countOf } from '@/lib/plural';

/** POS-I103: bulk-load customers from a CSV file (name, phone, email, address, city). */
const MAX_ROWS = 500;
const TEMPLATE = 'name,phone,email,address,city\nAhmed Ali,03001234567,ahmed@example.com,Shop 4 Main Bazaar,Lahore\n';

/** Minimal RFC-4180 style parser: quoted fields, doubled quotes, CRLF or LF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

interface ImportResult { created: number; failed: { line: number; name: string; reason: string }[]; total: number }

export function CustomerCsvImport() {
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File) => {
    setBusy(true);
    const failed: ImportResult['failed'] = [];
    let created = 0;
    try {
      const rows = parseCsv(await file.text());
      const header = (rows[0] ?? []).map((h) => h.trim().toLowerCase());
      const col = (...names: string[]) => header.findIndex((h) => names.some((n) => h === n || h.includes(n)));
      const idx = { name: col('name'), phone: col('phone', 'mobile'), email: col('email'), address: col('address'), city: col('city') };
      if (idx.name < 0) {
        setResult({ created: 0, total: 0, failed: [{ line: 1, name: '', reason: 'The first row must be a header with a "name" column' }] });
        return;
      }
      const body = rows.slice(1, MAX_ROWS + 1);
      for (let i = 0; i < body.length; i++) {
        const r = body[i];
        const get = (j: number) => (j >= 0 ? (r[j] ?? '').trim() : '');
        const name = get(idx.name);
        const line = i + 2;
        setProgress(`${i + 1} / ${body.length}`);
        if (!name) { failed.push({ line, name: '', reason: 'Name is empty' }); continue; }
        const payload: Record<string, string> = { name };
        for (const k of ['phone', 'email', 'address', 'city'] as const) {
          const v = get(idx[k]);
          if (v) payload[k] = v;
        }
        try {
          await api.post('/customers', payload);
          created++;
        } catch (err) {
          const data = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data;
          const msg = Array.isArray(data?.message) ? data?.message.join('; ') : data?.message;
          failed.push({ line, name, reason: msg ?? 'Could not be saved' });
        }
      }
      if (rows.length - 1 > MAX_ROWS) {
        failed.push({ line: MAX_ROWS + 2, name: '', reason: `Only the first ${MAX_ROWS} rows are imported at a time; import the rest in another file` });
      }
      setResult({ created, failed, total: body.length });
      qc.invalidateQueries({ queryKey: ['customers'] });
    } finally {
      setBusy(false);
      setProgress('');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const downloadErrors = () => {
    if (!result) return;
    const csv = 'line,name,reason\n' + result.failed.map((f) => [f.line, `"${f.name.replace(/"/g, '""')}"`, `"${f.reason.replace(/"/g, '""')}"`].join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers-import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
      />
      <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
        <Upload className="me-1.5 h-4 w-4" />
        {busy ? `Importing… ${progress}` : 'Import CSV'}
      </Button>
      <Button variant="ghost" size="sm" onClick={downloadTemplate} disabled={busy}>CSV template</Button>
      <Dialog header="Import customers" visible={!!result} onHide={() => setResult(null)} className="w-full max-w-lg">
        {result && (
          <div className="space-y-3 text-sm">
            <p>
              <strong>{result.created}</strong> of {countOf(result.total, 'customer', 'customers')} imported
              {result.failed.length > 0 ? `, ${countOf(result.failed.length, 'problem', 'problems')}` : ''}.
            </p>
            {result.failed.length > 0 && (
              <>
                <ul className="max-h-56 overflow-y-auto space-y-1 rounded-md border p-2">
                  {result.failed.map((f, i) => (
                    <li key={i}>Line {f.line}{f.name ? ` (${f.name})` : ''}: {f.reason}</li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" onClick={downloadErrors}>Download the problem list</Button>
              </>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" size="sm" onClick={downloadTemplate}>Download CSV template</Button>
              <Button onClick={() => setResult(null)}>Close</Button>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
}
